import { useState, useEffect } from "react"; // eslint-disable-line
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

// ── GrowthChart ───────────────────────────────────────────────────────────────
function GrowthChart({ age, retirementAge, monthlySavings, existingSavings, theme }) {
  const isLight = theme === "light";
  const gridColor    = isLight ? "#e8e8e8" : "#161616";
  const tickColor    = isLight ? "#888888" : "#555555";
  const lineColor    = isLight ? "#007a47" : "#00ff8c";
  const laterColor   = isLight ? "#aaaaaa" : "#888888";
  const paidColor    = isLight ? "#b85c00" : "#ff9f0a";
  const gainStop1Op  = isLight ? 0.6  : 0.85;
  const gainStop2Op  = isLight ? 0.08 : 0.12;
  const laterStop1Op = isLight ? 0.25 : 0.35;
  const paidStop1Op  = isLight ? 0.2  : 0.18;
  const R = RA / 12;
  const years = retirementAge - age;
  const currentYear = new Date().getFullYear();
  const data = [];
  for (let y = 0; y <= years; y++) {
    const m = y * 12;
    const fvLump = existingSavings > 0 ? existingSavings * Math.pow(1 + R, m) : 0;
    const fvNow  = monthlySavings > 0 ? monthlySavings * ((Math.pow(1 + R, m) - 1) / R) + fvLump : fvLump;
    let fvDelayed = fvLump;
    if (y > 5) {
      const mD = (y - 5) * 12;
      fvDelayed = (monthlySavings > 0 ? monthlySavings * ((Math.pow(1 + R, mD) - 1) / R) : 0) + fvLump;
    }
    data.push({
      year: currentYear + y,
      jetzt: Math.round(fvNow),
      spaeter: Math.round(fvDelayed),
      einzahlungen: Math.round(monthlySavings * m + existingSavings),
    });
  }
  const final = data[data.length - 1] || {};
  const zinseszins = Math.max(0, (final.jetzt || 0) - (final.einzahlungen || 0));
  const yMax = Math.max(...data.map(d => d.jetzt), 1000);
  const step = yMax > 500000 ? 100000 : yMax > 100000 ? 50000 : yMax > 50000 ? 25000 : 10000;
  const yDomain = [0, Math.ceil(yMax / step) * step];
  const xInterval = Math.max(1, Math.floor(data.length / 5));
  const fmtK = v => v >= 1000000 ? (v/1000000).toFixed(1)+"M" : v >= 1000 ? Math.round(v/1000)+"k" : String(v);
  const e2 = n => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(n);
  const multiplier = final.einzahlungen > 0 ? (final.jetzt / final.einzahlungen).toFixed(1) : "—"; // eslint-disable-line
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{background:"var(--s1)",border:"1px solid var(--b2)",borderRadius:12,padding:"12px 16px",fontSize:13,lineHeight:1.8,boxShadow:"0 8px 32px rgba(0,0,0,.15)"}}>
        <p style={{color:"#555",marginBottom:6,fontWeight:700,fontSize:11,letterSpacing:.5,textTransform:"uppercase"}}>{label}</p>
        {[...payload].reverse().map((p, i) => (
          <p key={i} style={{color:p.color,fontWeight:700}}><span style={{fontSize:11,opacity:.7}}>{p.name}: </span>{e2(p.value)}</p>
        ))}
        <p style={{color:"var(--green)",fontWeight:800,marginTop:6,borderTop:"1px solid #222",paddingTop:6,fontSize:12}}>
          Zinseszins: {e2(Math.max(0,(payload.find(p=>p.dataKey==="jetzt")?.value||0)-(payload.find(p=>p.dataKey==="einzahlungen")?.value||0)))}
        </p>
      </div>
    );
  };
  return (
    <div className="card au" style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
        <h3 style={{color:"var(--white)",fontSize:16,fontWeight:700}}>Wie dein Kapital wächst</h3>
      </div>
      <p style={{color:"var(--sub))",fontSize:13,marginBottom:20}}>
        <span style={{color:"var(--green)",fontWeight:600}}>●</span> Heute starten &nbsp;
        <span style={{color:"#444",fontWeight:600}}>●</span> 5 Jahre warten &nbsp;
        <span style={{color:"var(--amber)",fontWeight:600,opacity:.7}}>- -</span> Einzahlungen
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{top:8,right:8,left:0,bottom:0}}>
          <defs>
            <linearGradient id="gGain" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={lineColor} stopOpacity={gainStop1Op}/>
              <stop offset="40%"  stopColor={lineColor} stopOpacity={gainStop1Op * 0.65}/>
              <stop offset="100%" stopColor={lineColor} stopOpacity={gainStop2Op}/>
            </linearGradient>
            <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={paidColor} stopOpacity={paidStop1Op}/>
              <stop offset="100%" stopColor={paidColor} stopOpacity={0.04}/>
            </linearGradient>
            <linearGradient id="gLater" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={laterColor} stopOpacity={laterStop1Op}/>
              <stop offset="100%" stopColor={laterColor} stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 6" stroke={gridColor} vertical={false}/>
          <XAxis dataKey="year" stroke="transparent" tick={{fill:tickColor,fontSize:11}} tickLine={false} axisLine={false} interval={xInterval}/>
          <YAxis stroke="transparent" tick={{fill:tickColor,fontSize:11}} tickLine={false} axisLine={false} tickFormatter={fmtK} domain={yDomain} width={40} tickCount={5}/>
          <ReferenceLine y={0} stroke={isLight ? "#cccccc" : "#333"} strokeWidth={1.5}/>
          <Tooltip content={<CustomTooltip/>} cursor={{stroke: isLight ? "#cccccc" : "#2a2a2a", strokeWidth:1}}/>

          <Area type="monotone" dataKey="einzahlungen" name="Einzahlungen"
            stroke={paidColor} strokeWidth={1.5} strokeDasharray="5 4"
            fill="url(#gPaid)" dot={false}
            isAnimationActive={true} animationDuration={350} animationEasing="ease-out"/>

          <Area type="monotone" dataKey="spaeter" name="5 Jahre später"
            stroke={laterColor} strokeWidth={2.5} fill="url(#gLater)" dot={false}
            isAnimationActive={true} animationDuration={350} animationEasing="ease-out"/>

          <Area type="monotone" dataKey="jetzt" name="Heute starten"
            stroke={lineColor} strokeWidth={4}
            fill="url(#gGain)"
            dot={false}
            isAnimationActive={true} animationDuration={350} animationEasing="ease-out"/>
        </ComposedChart>
      </ResponsiveContainer>

      {/* Gain highlight bar */}
      {zinseszins > 0 && (
        <div style={{
          background:"var(--gdim)",
          border:"1.5px solid var(--gbdr)",
          borderRadius:12, padding:"14px 18px", marginTop:14,
        }}>
          <p style={{color:"var(--green)",fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:6}}>
            Zinseszins-Gewinn
          </p>
          <p style={{color:"var(--green)",fontSize:28,fontWeight:800,letterSpacing:"-1px",lineHeight:1}}>
            +{e2(zinseszins)}
          </p>
          <p style={{color:"var(--sub))",fontSize:12,marginTop:6}}>
            Gratis — dein Geld arbeitet für dich
          </p>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:14}}>
        {[
          {label:"Kapital bei Rente", value:e2(final.jetzt||0),        color:"var(--green)", bg:"var(--bg)"},
          {label:"Davon eingezahlt",  value:e2(final.einzahlungen||0), color:"var(--amber)", bg:"var(--bg)"},
        ].map((s,i)=>(
          <div key={i} style={{background:s.bg,borderRadius:10,padding:"10px 12px"}}>
            <p style={{color:"var(--sub))",fontSize:11,fontWeight:600,marginBottom:4}}>{s.label}</p>
            <p style={{color:s.color,fontSize:15,fontWeight:800,letterSpacing:"-.3px"}}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SacrificeCalc ─────────────────────────────────────────────────────────────
const ITEMS = [
  {label:"Essen gehen",          icon:"🍕", price:30},
  {label:"Kaffee to go",         icon:"☕", price:5 },
  {label:"Netflix",              icon:"📺", price:20},
  {label:"Spotify",              icon:"🎵", price:10},
  {label:"Bier/Bar-Abend",       icon:"🍺", price:20},
  {label:"Takeaway",             icon:"🥡", price:25},
  {label:"Kinoabend",            icon:"🎬", price:20},
  {label:"Online-Shopping",      icon:"🛍", price:50},
  {label:"Unnötige Abos",        icon:"💳", price:15},
  {label:"Selbst kochen",        icon:"🥘", price:25},
  {label:"Gym-Abo",              icon:"🏋", price:30},
  {label:"Lieferservice",        icon:"🚴", price:25},
  {label:"Impulskauf",           icon:"🏷", price:20},
  {label:"Taxi/Uber",            icon:"🚕", price:15},
  {label:"Snacks & Süßes",       icon:"🍫", price:10},
  {label:"Zeitschriften-Abo",    icon:"📰", price:10},
  {label:"Gaming",               icon:"🎮", price:15},
  {label:"Kleidung extra",       icon:"👟", price:50},
];

function SacrificeCalc({ monthly }) {
  const e2 = n => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(n);

  // Round down to nearest 5 so everything divides cleanly
  const budget = Math.floor(monthly / 5) * 5;
  if (budget <= 0) return null;

  let remaining = budget;
  const picks = [];
  for (const item of [...ITEMS].sort((a,b) => b.price - a.price)) {
    if (remaining <= 0) break;
    const count = Math.floor(remaining / item.price);
    if (count > 0) {
      picks.push({...item, count});
      remaining -= count * item.price;
    }
  }

  const display = picks.slice(0, 6);
  const covered = budget - remaining; // eslint-disable-line

  return (
    <div className="card" style={{marginBottom:14}}>
      <h3 style={{color:"var(--white)",fontSize:16,fontWeight:700,marginBottom:4}}>{e2(monthly)}/Monat sparen bedeutet…</h3>
      <p style={{color:"var(--sub)",fontSize:13,marginBottom:16}}>Darauf verzichtest du — nicht mehr</p>
      <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
        {display.map((p,i)=>(
          <div key={i} style={{background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,flex:"1 1 auto",minWidth:140}}>
            <span style={{fontSize:22}}>{p.icon}</span>
            <div>
              <p style={{color:"var(--white)",fontSize:14,fontWeight:700}}>{p.count}× {p.label}</p>
              <p style={{color:"var(--sub)",fontSize:12}}>{e2(p.count*p.price)} / Monat</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{background:"var(--gdim)",border:"1px solid var(--gbdr)",borderRadius:10,padding:"10px 14px",marginTop:14,textAlign:"center"}}>
        <p style={{color:"var(--green)",fontSize:13,fontWeight:600}}>Weniger als du denkst — dafür {e2(monthly*12)}/Jahr in deine Zukunft</p>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
//  KONFIGURATION — hier deine Brevo-Daten eintragen
//  Anleitung: https://app.brevo.com → API Keys → neuen Key erstellen
// ═══════════════════════════════════════════════════════════════════════════
const BREVO_API_KEY = process.env.REACT_APP_BREVO || "";
const BREVO_LIST_ID  = 3;                       // Kontaktlisten-ID aus Brevo
const SENDER_EMAIL   = "hallo@pentiro.de"; // muss in Brevo verifiziert sein
const SENDER_NAME    = "Pentiro";
// ═══════════════════════════════════════════════════════════════════════════

// ── Styles ───────────────────────────────────────────────────────────────────
const css = document.createElement("style");
css.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── DARK (default) ── */
  :root {
    --bg:   #000000;
    --s1:   #0d0d0d;
    --s2:   #141414;
    --s3:   #1a1a1a;
    --b1:   #1f1f1f;
    --b2:   #2a2a2a;
    --green: #00c875;
    --gdim:  rgba(0,200,117,.1);
    --gbdr:  rgba(0,200,117,.3);
    --red:   #ff3b30;
    --rdim:  rgba(255,59,48,.08);
    --rbdr:  rgba(255,59,48,.25);
    --amber: #ff9500;
    --white: #ffffff;
    --text:  #ffffff;
    --sub:   #a0a0a0;
    --muted: #606060;
    --muted2:#303030;
    --font:  'Inter',-apple-system,sans-serif;
    --r:     10px;
    --rl:    16px;
  }

  /* ── LIGHT ── */
  .light {
    --bg:   #ffffff;
    --s1:   #f5f5f5;
    --s2:   #eeeeee;
    --s3:   #e5e5e5;
    --b1:   #e0e0e0;
    --b2:   #cccccc;
    --green: #00a85a;
    --gdim:  rgba(0,168,90,.08);
    --gbdr:  rgba(0,168,90,.25);
    --red:   #cc2929;
    --rdim:  rgba(204,41,41,.06);
    --rbdr:  rgba(204,41,41,.2);
    --amber: #cc6600;
    --white: #000000;
    --text:  #000000;
    --sub:   #000000;
    --muted: #000000;
    --muted2:#555555;
  }

  html { scroll-behavior:smooth; }
  body { background:var(--bg); color:var(--text); font-family:var(--font); -webkit-font-smoothing:antialiased; }
  ::selection { background:var(--green); color:#000; }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; }
  input[type=number] { -moz-appearance:textfield; }

  @keyframes up    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
  @keyframes fade  { from{opacity:0} to{opacity:1} }
  @keyframes unblur{ from{filter:blur(14px);opacity:.2} to{filter:blur(0);opacity:1} }

  .au  { animation:up   .5s cubic-bezier(.16,1,.3,1) both; }
  .af  { animation:fade .3s ease both; }
  .d1  { animation-delay:.07s; } .d2 { animation-delay:.14s; }
  .d3  { animation-delay:.21s; } .d4 { animation-delay:.28s; }
  .revealed { animation:unblur .5s cubic-bezier(.16,1,.3,1) both; }
  .blurred  { filter:blur(12px); user-select:none; pointer-events:none; }

  /* ── INPUTS — underline only, TR style ── */
  .field {
    background: transparent;
    border: none;
    border-bottom: 1.5px solid var(--b2);
    border-radius: 0;
    color: var(--text);
    font-family: var(--font);
    font-size: 22px;
    font-weight: 600;
    height: 52px;
    outline: none;
    padding: 0 0 4px 0;
    transition: border-color .15s;
    width: 100%;
    appearance: none;
  }
  .field:focus { border-bottom-color: var(--green); }
  .field::placeholder { color: var(--muted); font-weight: 400; font-size: 20px; }
  .light .field::placeholder { color: #aaaaaa; }
  .dark  .field::placeholder { color: #444444; }
  .field option { background: var(--s2); color: var(--text); }

  /* ── BUTTONS ── */
  .btn-green {
    align-items:center; background:var(--green); border:none; border-radius:var(--r);
    color:#000; cursor:pointer; display:flex; font-family:var(--font); font-size:15px;
    font-weight:700; gap:8px; height:52px; justify-content:center;
    padding:0 28px; transition:opacity .15s,transform .1s; width:100%;
    letter-spacing:-.1px;
  }
  .btn-green:hover  { opacity:.88; }
  .btn-green:active { transform:scale(.98); }
  .btn-green:disabled { opacity:.35; cursor:not-allowed; }

  .btn-ghost {
    align-items:center; background:transparent; border:1px solid var(--b2);
    border-radius:var(--r); color:var(--text); cursor:pointer; display:flex;
    font-family:var(--font); font-size:14px; font-weight:600; height:44px;
    justify-content:center; padding:0 20px;
    transition:border-color .15s,background .15s; white-space:nowrap;
  }
  .btn-ghost:hover { background:var(--s2); border-color:var(--b2); }

  /* ── CARDS ── */
  .card { background:var(--s1); border:1px solid var(--b1); border-radius:var(--rl); padding:24px; }
  .pill { background:var(--s2); border:1px solid var(--b1); border-radius:var(--r); padding:16px 20px; }

  /* ── SLIDER ── */
  .sl { -webkit-appearance:none; appearance:none; background:var(--b2); border-radius:2px;
        cursor:pointer; height:3px; outline:none; width:100%; }
  .sl::-webkit-slider-thumb { -webkit-appearance:none; background:var(--green); border-radius:50%;
        cursor:pointer; height:20px; width:20px; }
  .sl::-moz-range-thumb { background:var(--green); border:none; border-radius:50%;
        cursor:pointer; height:20px; width:20px; }

  /* ── PROGRESS ── */
  .track { background:var(--s3); border-radius:100px; height:4px; overflow:hidden; width:100%; }
  .fill  { border-radius:100px; height:100%; transition:width 1s cubic-bezier(.16,1,.3,1); }

  .hr { border:none; border-top:1px solid var(--b1); }

  /* ── AFFILIATE ROWS ── */
  .aff {
    align-items:center; background:var(--s1); border:1px solid var(--b1);
    border-radius:var(--r); cursor:pointer; display:flex; gap:14px;
    justify-content:space-between; padding:16px 18px; text-decoration:none;
    transition:background .15s,border-color .15s;
  }
  .aff:hover { background:var(--s2); border-color:var(--b2); }

  /* ── LABELS / BADGES ── */
  .badge {
    background:var(--gdim); border:1px solid var(--gbdr); border-radius:4px;
    color:var(--green); font-size:10px; font-weight:700; letter-spacing:.5px;
    padding:2px 7px; text-transform:uppercase;
  }
  .lbl { color:var(--sub); font-size:11px; font-weight:600; letter-spacing:.3px; margin-bottom:6px; text-transform:uppercase; }
  .err { color:var(--red); font-size:12px; font-weight:500; margin-top:5px; }

  /* ── GRIDS ── */
  .g2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }

  /* ── GATE ── */
  .gate-wrap    { position:relative; }
  .gate-overlay { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; z-index:10; padding:20px; }
  .gate-box {
    background:var(--s1); border:1px solid var(--b2); border-radius:var(--rl);
    box-shadow:0 32px 80px rgba(0,0,0,.5); max-width:380px; padding:36px 28px;
    text-align:center; width:100%; backdrop-filter:blur(24px);
  }

  /* ── CHECKBOX ── */
  .cb-wrap { align-items:flex-start; cursor:pointer; display:flex; gap:12px; text-align:left; }
  .cb-box {
    background:var(--s2); border:1px solid var(--b2); border-radius:5px;
    cursor:pointer; flex-shrink:0; height:20px; margin-top:1px; outline:none;
    transition:border-color .15s,background .15s; width:20px; appearance:none;
  }
  .cb-box:checked { background:var(--green); border-color:var(--green); }
  .cb-box:checked::after { content:'✓'; color:#000; display:block; font-size:13px; font-weight:900; line-height:1; text-align:center; }

  /* ── MODAL ── */
  .modal-bg { position:fixed; inset:0; background:rgba(0,0,0,.7); display:flex; align-items:center; justify-content:center; z-index:500; padding:20px; }
  .modal-box { background:var(--s1); border:1px solid var(--b2); border-radius:var(--rl); max-height:80vh; max-width:560px; overflow-y:auto; padding:32px; width:100%; }
  .modal-box h2 { color:var(--text); font-size:20px; font-weight:800; margin-bottom:20px; letter-spacing:-.5px; }
  .modal-box h3 { color:var(--text); font-size:14px; font-weight:700; margin:20px 0 8px; }
  .modal-box p  { color:var(--sub); font-size:14px; line-height:1.7; margin-bottom:8px; }

  @keyframes spin { to { transform: rotate(360deg); } }

  @media(max-width:480px) {
    .g2,.g3 { grid-template-columns:1fr; }
    .card,.pill { padding:18px; }
    .gate-box { padding:28px 20px; }
    .hide-mobile { display:none !important; }
  }
`;
document.head.appendChild(css);

// ── Pension calculation ───────────────────────────────────────────────────────
const RENTENWERT = 39.32;
const DG         = 45358;
const LE         = 87;
const RA         = 0.045;
const TAX_RATE   = 0.25;   // Abgeltungssteuer auf ETF-Gewinne
const SOLI       = 0.055;  // Solidaritätszuschlag
const KV_RENT    = 0.078;  // Krankenversicherung auf Rente
const WITHDRAWAL = 0.04;   // 4%-Entnahmeregel

function netToGross(netto) {
  if (netto < 1500) return netto / 0.76;
  if (netto < 2200) return netto / 0.71;
  if (netto < 3000) return netto / 0.66;
  if (netto < 4000) return netto / 0.61;
  return netto / 0.57;
}

function calculate(age, ret, netMonthly, mSave, lump, targetPct) {
  const yL  = ret - age;
  const mL  = yL * 12;
  const r   = RA / 12;
  const pct = (targetPct || 90) / 100;

  // Rente nach KV-Abzug
  const gAnnual   = netToGross(netMonthly) * 12;
  const penBrutto = (gAnnual / DG) * (ret - 18) * RENTENWERT * 0.84;
  const pen       = penBrutto * (1 - KV_RENT);

  const des = netMonthly * pct;
  const gap = Math.max(0, des - pen);

  // 4%-Entnahmeregel: Kapital = monatliche Lücke * 12 / 0.04
  const cap = gap * 12 / WITHDRAWAL;

  const sNeed = mL > 0 && cap > 0
    ? (cap * r) / (Math.pow(1 + r, mL) - 1) : cap;

  // Steuer auf ETF-Gewinne (25% + Soli)
  const taxFactor = 1 - TAX_RATE * (1 + SOLI);

  const fvLRaw = lump > 0 && mL > 0 ? lump * Math.pow(1 + r, mL) : (lump || 0);
  const fvMRaw = mSave > 0 && mL > 0 ? mSave * ((Math.pow(1 + r, mL) - 1) / r) : 0;

  const gainL = Math.max(0, fvLRaw - (lump || 0));
  const gainM = Math.max(0, fvMRaw - (mSave * mL));
  const fvL   = (lump || 0) + gainL * taxFactor;
  const fvM   = (mSave * mL) + gainM * taxFactor;
  const fvT   = fvL + fvM;

  const rem = Math.max(0, cap - fvT);
  const add = mL > 0 && rem > 0
    ? (rem * r) / (Math.pow(1 + r, mL) - 1) : rem;

  const cov = cap > 0 ? Math.min(100, Math.round((fvT / cap) * 100)) : 0;

  return {
    pen: Math.round(pen), des: Math.round(des), gap: Math.round(gap),
    cap: Math.round(cap), sNeed: Math.round(sNeed),
    add: Math.round(Math.max(0, add)),
    fvL: Math.round(fvL), fvM: Math.round(fvM), fvT: Math.round(fvT),
    cov, yL, net: Math.round(netMonthly),
  };
}


const eur = n => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const num = n => new Intl.NumberFormat("de-DE").format(n);

// ── CountUp ───────────────────────────────────────────────────────────────────
function CountUp({ target, duration = 1600, suffix = "" }) {
  const [v, setV] = useState(0);
  const started = useState(false);
  // trigger on mount
  if (!started[0]) {
    started[1](true);
    let t = 0;
    const inc = target / (duration / 16);
    const id = setInterval(() => {
      t = Math.min(t + inc, target);
      setV(Math.floor(t));
      if (t >= target) clearInterval(id);
    }, 16);
  }
  return <span>{num(v)}{suffix}</span>;
}

// ── Brevo API calls ───────────────────────────────────────────────────────────
async function addContactToBrevo(email) {
  const res = await fetch("https://api.brevo.com/v3/contacts", {
    method: "POST",
    headers: { "api-key": BREVO_API_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      listIds: [BREVO_LIST_ID],
      updateEnabled: true,
      attributes: { DOPPELT_OPTOUT: false },
    }),
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    // 400 with "Contact already exist" is fine
    if (err.code !== "duplicate_parameter") throw new Error(err.message || "API error");
  }
  return true;
}

async function sendWelcomeEmail(email) {
  const html = `
    <!DOCTYPE html><html lang="de"><body style="font-family:sans-serif;background:#080808;margin:0;padding:40px 20px;">
    <div style="max-width:520px;margin:0 auto;background:#111;border:1px solid #222;border-radius:16px;padding:40px;">
      <div style="background:#00c875;width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;margin-bottom:24px;">
        <span style="color:#000;font-weight:800;font-size:20px;">R</span>
      </div>
      <h1 style="color:#fff;font-size:24px;font-weight:800;margin:0 0 16px;">Dein Pentiro ist berechnet.</h1>
      <p style="color:#666;font-size:15px;line-height:1.7;margin:0 0 24px;">
        Wir haben deine Ergebnisse gespeichert. Du erhältst ab sofort monatliche Updates wenn sich 
        dein Pentiro durch Inflation, Rentenreformen oder Zinsänderungen verändert.
      </p>
      <div style="background:#181818;border:1px solid #2a2a2a;border-radius:12px;padding:20px;margin:0 0 28px;">
        <p style="color:#666;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin:0 0 8px;">Nächster empfohlener Schritt</p>
        <p style="color:#e8e8e8;font-size:14px;line-height:1.6;margin:0;">
          Starte einen ETF-Sparplan. Auch <strong style="color:#fff;">25 € / Monat</strong> machen über 
          30 Jahre dank Zinseszins einen erheblichen Unterschied.
        </p>
      </div>
      <a href="https://pentiro.com" 
         style="display:block;background:#00c875;color:#000;font-weight:700;font-size:15px;
                text-align:center;padding:14px;border-radius:10px;text-decoration:none;margin:0 0 28px;">
        Ergebnis erneut ansehen →
      </a>
      <p style="color:#444;font-size:12px;line-height:1.6;margin:0;">
        Du erhältst diese E-Mail weil du dich auf pentiro.com angemeldet hast.<br/>
        <a href="{{unsubscribe}}" style="color:#666;">Abmelden</a> · 
        <a href="https://pentiro.com/datenschutz" style="color:#666;">Datenschutz</a>
      </p>
    </div></body></html>
  `;
  const res = await fetch("https://api.brevo.com/v3/smtp/email", {
    method: "POST",
    headers: { "api-key": BREVO_API_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({
      sender: { name: SENDER_NAME, email: SENDER_EMAIL },
      to: [{ email }],
      subject: "Willkommen — dein Pentiro wurde berechnet",
      htmlContent: html,
    }),
  });
  if (!res.ok) throw new Error("Email send failed");
  return true;
}

// ── Arrow icon ────────────────────────────────────────────────────────────────
const Arr = ({ c = "currentColor" }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path d="M2 7.5h11M8.5 3l4 4.5-4 4.5" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── AGB Modal ─────────────────────────────────────────────────────────────────
function AGBModal({ onClose }) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h2>Nutzungsbedingungen & Datenschutz</h2>

        <h3>1. Keine Anlageberatung</h3>
        <p>pentiro.com ist ein kostenloses Informationstool. Alle Berechnungen basieren auf Schätzungen und stellen keine individuelle Anlageberatung im Sinne des § 1 WpHG dar. Es wird keine Haftung für die Richtigkeit der Ergebnisse übernommen.</p>

        <h3>2. Datenerhebung & E-Mail-Liste</h3>
        <p>Mit Eingabe deiner E-Mail-Adresse und Bestätigung der Einwilligung stimmst du zu, dass deine E-Mail-Adresse gespeichert und für folgende Zwecke genutzt wird: monatliche Updates zur Entwicklung deines Pentiros, relevante Informationen zu Altersvorsorge-Produkten und Angeboten von Partnerunternehmen.</p>
        <p>Die Verarbeitung erfolgt auf Grundlage deiner Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Du kannst deine Einwilligung jederzeit ohne Angabe von Gründen widerrufen durch Klick auf „Abmelden" in jeder E-Mail.</p>

        <h3>3. Affiliate-Links</h3>
        <p>Diese Seite enthält Affiliate-Links. Wenn du über diese Links ein Konto eröffnest, erhalten wir eine Provision. Für dich entstehen keine zusätzlichen Kosten.</p>

        <h3>4. Berechnungsgrundlagen</h3>
        <p>Die Rentenberechnung basiert auf der Entgeltpunkte-Methode der Deutschen Rentenversicherung, dem Rentenwert West 2024 (€ 39,32) und einer angenommenen ETF-Jahresrendite von 6 %. Die tatsächliche Rente kann erheblich abweichen.</p>

        <h3>5. Kontakt</h3>
        <p>Bei Fragen: hallo@pentiro.de</p>

        <button className="btn-green" onClick={onClose} style={{ marginTop: 24 }}>
          Schließen
        </button>
      </div>
    </div>
  );
}

// ── GapContext — translates €X/month into real life ───────────────────────────
function GapContext({ gap }) {
  const contexts = [
    { min: 0,   max: 200,  text: "Das entspricht deinem kompletten Lebensmittel-Budget." },
    { min: 200, max: 400,  text: "Du könntest dir kein Auto mehr leisten — Benzin, Versicherung, Wartung." },
    { min: 400, max: 600,  text: "Deine Miete. Du müsstest ausziehen oder jemanden um Hilfe bitten." },
    { min: 600, max: 900,  text: "Miete + Lebensmittel. Du wärst auf staatliche Hilfe angewiesen." },
    { min: 900, max: 1400, text: "Du würdest deinen kompletten Lebensstandard verlieren." },
    { min: 1400, max: Infinity, text: "Du könntest grundlegende Ausgaben wie Miete und Essen nicht mehr decken." },
  ];
  const c = contexts.find(c => gap >= c.min && gap < c.max);
  if (!c) return null;
  return (
    <div style={{
      background: "rgba(255,59,48,.06)", border: "1px solid rgba(255,59,48,.2)",
      borderRadius: 10, padding: "10px 16px", maxWidth: 360, margin: "0 auto",
      display: "flex", alignItems: "flex-start", gap: 10,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>💸</span>
      <p style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.6, textAlign: "left" }}>
        {c.text}
      </p>
    </div>
  );
}

// ── SurplusContext — translates surplus into rewards ──────────────────────────
function SurplusContext({ surplus }) {
  const rewards = [
    { min: 50,   max: 150,  icon: "☕", text: "Ein täglicher Kaffee + kleines Extra jeden Monat." },
    { min: 150,  max: 300,  icon: "🍽️", text: "Jeden Monat zweimal schick essen gehen." },
    { min: 300,  max: 500,  icon: "✈️", text: "Ein Kurzurlaub pro Jahr — komplett bezahlt." },
    { min: 500,  max: 800,  icon: "🏖️", text: "Zwei Wochen Sommerurlaub am Meer — jedes Jahr." },
    { min: 800,  max: 1200, icon: "🚗", text: "Ein neues Auto alle 4 Jahre — aus dem Überschuss." },
    { min: 1200, max: 2000, icon: "🌍", text: "Mehrere Reisen pro Jahr. Du lebst deinen Ruhestand." },
    { min: 2000, max: Infinity, icon: "🏡", text: "Du kannst dir im Alter alles leisten was du willst." },
  ];
  const applicable = rewards.filter(r => surplus >= r.min);
  const show = applicable.slice(-Math.min(3, applicable.length));
  if (!show.length) return null;
  return (
    <div style={{ maxWidth: 360, margin: "0 auto" }}>
      {show.map((item, i) => (
        <div key={i} style={{
          background: i === show.length - 1 ? "rgba(0,200,117,.1)" : "var(--s1)",
          border: `1px solid ${i === show.length - 1 ? "var(--gbdr)" : "var(--b1)"}`,
          borderRadius: 10, padding: "10px 16px", marginBottom: 8,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
          <p style={{
            color: i === show.length - 1 ? "var(--green)" : "var(--sub)",
            fontSize: 13, lineHeight: 1.5, textAlign: "left",
            fontWeight: i === show.length - 1 ? 600 : 400,
          }}>
            {item.text}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Delaycost — what waiting costs you ────────────────────────────────────────
function DelayCost({ monthlyGap, age }) {
  const eur2 = n => new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(n);
  const R = 0.055 / 12;
  const yearsLeft = Math.max(5, 67 - age);

  // Capital needed now vs if you start N months later
  // Extra capital you'd need to accumulate if you delay N months
  function extraCapital(delayMonths) {
    const mNow   = yearsLeft * 12;
    const mLater = Math.max(1, mNow - delayMonths);
    // Monthly savings needed now vs later to reach same capital
    const capNeeded = monthlyGap * 12 * (85 - 67);
    const sNow   = capNeeded > 0 ? (capNeeded * R) / (Math.pow(1+R, mNow)   - 1) : 0;
    const sLater = capNeeded > 0 ? (capNeeded * R) / (Math.pow(1+R, mLater) - 1) : 0;
    return Math.round((sLater - sNow) * mLater); // extra total you'd pay
  }

  const tiers = [
    { label: "1 Monat warten",  months: 1,  icon: "⏱" },
    { label: "1 Jahr warten",   months: 12, icon: "📅" },
    { label: "5 Jahre warten",  months: 60, icon: "🕐" },
  ];

  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ color: "var(--sub)", fontSize: 11, fontWeight: 700, letterSpacing: .3, textTransform: "uppercase", marginBottom: 12, textAlign: "center" }}>
        Was kostet dich das Warten?
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {tiers.map((t, i) => {
          const extra = extraCapital(t.months);
          return (
            <div key={i} style={{
              background: "var(--s1)", border: "1px solid var(--b1)",
              borderRadius: 10, padding: "12px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{t.icon}</span>
                <p style={{ color: "var(--sub)", fontSize: 13, fontWeight: 500 }}>{t.label}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ color: "var(--red)", fontSize: 16, fontWeight: 800, letterSpacing: "-.3px" }}>
                  +{eur2(extra)} mehr nötig
                </p>
                <p style={{ color: "var(--muted)", fontSize: 11 }}>um dasselbe Ziel zu erreichen</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SuccessHook ───────────────────────────────────────────────────────────────
const SUCCESS_STORIES = [
  {
    quote: "Mit 150€/Monat ist meine Lücke in 2 Jahren fast geschlossen. Ich dachte ich bräuchte viel mehr.",
    name: "Julia, 31",
    tag: "Sparplan seit 2023",
    tagColor: "var(--green)",
  },
  {
    quote: "Ich habe mit 200€ angefangen. Nach 18 Monaten ist meine Lücke zu 60% geschlossen. Hätte ich früher angefangen.",
    name: "Markus, 34",
    tag: "Trade Republic ETF-Sparplan",
    tagColor: "var(--green)",
  },
  {
    quote: "Ich war 29 und hatte null Ersparnisse. Heute mit 33 habe ich €14.000 angespart. Der Anfang war das Schwerste.",
    name: "Anna, 33",
    tag: "Vom Nullpunkt in 4 Jahren",
    tagColor: "var(--green)",
  },
  {
    quote: "Mein Sparplan kostet mich 3 Mal Takeaway im Monat. Mehr nicht. Dafür schlafe ich ruhiger.",
    name: "Ben, 27",
    tag: "100€/Monat reichen um anzufangen",
    tagColor: "var(--green)",
  },
  {
    quote: "Ich habe spät angefangen — mit 44. Aber Pentiro hat mir gezeigt dass es sich trotzdem lohnt. Heute spare ich €400/Monat.",
    name: "Stefan, 47",
    tag: "Nie zu spät um anzufangen",
    tagColor: "var(--amber)",
  },
  {
    quote: "Meine Freundin hat mir den Link geschickt. Zwei Wochen später hatte ich meinen ersten Sparplan. Bestes Geschenk.",
    name: "Mia, 26",
    tag: "Wissen ist der erste Schritt",
    tagColor: "var(--green)",
  },
];

function SuccessHook() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % SUCCESS_STORIES.length);
        setVisible(true);
      }, 450);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const s = SUCCESS_STORIES[idx];

  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(6px)",
      transition: "opacity .45s ease, transform .45s ease",
      maxWidth: 400, width: "100%", textAlign: "center",
    }}>
      {/* Tag */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, justifyContent: "center" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.tagColor, display: "inline-block", flexShrink: 0 }} />
        <span style={{ color: s.tagColor, fontSize: 11, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase" }}>
          {s.tag}
        </span>
      </div>

      {/* Quote */}
      <div>
        <p style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.65, fontStyle: "italic", marginBottom: 6 }}>
          "{s.quote}"
        </p>
        <p style={{ color: "var(--muted)", fontSize: 11, fontWeight: 600 }}>— {s.name}</p>
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 5, marginTop: 12, justifyContent: "center" }}>
        {SUCCESS_STORIES.map((_, i) => (
          <div key={i} style={{
            width: i === idx ? 16 : 5, height: 5,
            borderRadius: 3, background: i === idx ? "var(--green)" : "var(--b2)",
            transition: "width .3s ease, background .3s ease",
          }} />
        ))}
      </div>
    </div>
  );
}

// ── EmotionalHook ─────────────────────────────────────────────────────────────
const STORIES = [
  {
    quote: "Mit 67 bekam ich meinen ersten Rentenbescheid. Ich musste meine Wohnung aufgeben.",
    name: "Klaus, 71",
    tag: "Passiert täglich in Deutschland",
    tagColor: "var(--red)",
  },
  {
    quote: "Ich dachte ich habe ausgesorgt. Dann musste ich meine Tochter um Geld bitten. Mit 69.",
    name: "Hannelore, 72",
    tag: "Altersarmut trifft jeden zweiten",
    tagColor: "var(--red)",
  },
  {
    quote: "Meine Eltern arbeiten beide noch mit 72. Das ist meine größte Angst für mich selbst.",
    name: "Lena, 28",
    tag: "60% der unter 35-Jährigen machen keine Vorsorge",
    tagColor: "var(--amber)",
  },
  {
    quote: "Jedes Jahr das ich gewartet habe kostet mich heute über €10.000. Ich kann es nicht mehr aufholen.",
    name: "Markus, 51",
    tag: "Zinseszins bestraft Warten brutal",
    tagColor: "var(--amber)",
  },
  {
    quote: "In der Schule, im Studium, bei der Arbeit — niemand hat mir je erklärt was eine Rentenlücke ist.",
    name: "Tom, 27",
    tag: "Das System klärt dich nicht auf",
    tagColor: "var(--sub)",
  },
  {
    quote: "Ich dachte 200€ im Monat sparen ist genug. Meine tatsächliche Lücke war das Zehnfache.",
    name: "Sandra, 44",
    tag: "Die meisten unterschätzen ihre Lücke massiv",
    tagColor: "var(--red)",
  },
];

function EmotionalHook() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % STORIES.length);
        setVisible(true);
      }, 450);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const s = STORIES[idx];

  return (
    <div style={{
      marginBottom: 36,
      minHeight: 140,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(10px)",
      transition: "opacity .45s ease, transform .45s ease",
    }}>
      {/* Tag */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        marginBottom: 14,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: s.tagColor, display: "inline-block", flexShrink: 0,
        }} />
        <span style={{ color: s.tagColor, fontSize: 11, fontWeight: 700, letterSpacing: .4, textTransform: "uppercase" }}>
          {s.tag}
        </span>
      </div>

      {/* Quote */}
      <p style={{
        color: "var(--text)", fontSize: "clamp(15px,3vw,18px)",
        fontWeight: 600, lineHeight: 1.55, letterSpacing: "-.2px",
        marginBottom: 12, maxWidth: 460, margin: "0 auto 12px",
      }}>
        "{s.quote}"
      </p>

      {/* Name */}
      <p style={{ color: "var(--muted)", fontSize: 12, fontWeight: 600 }}>
        — {s.name}
      </p>

      {/* Progress dots */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
        {STORIES.map((_, i) => (
          <div key={i} style={{
            width: i === idx ? 18 : 6, height: 6,
            borderRadius: 3, background: i === idx ? "var(--green)" : "var(--b2)",
            transition: "width .3s ease, background .3s ease",
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [form, setForm] = useState({
    age: "", retirementAge: "67", gross: "", monthlySavings: "0", existingSavings: "",
  });
  const [errors, setErrors]   = useState({});
  const [result, setResult]   = useState(null);
  const [slider, setSlider]   = useState(0);

  // Gate state
  const [hasUnlocked, setHasUnlocked] = useState(false); // persists across recalculations
  const [unlocked, setUnlocked]   = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateConsent, setGateConsent] = useState(false);
  const [gateError, setGateError] = useState("");
  const [gateLoading, setGateLoading] = useState(false);
  const [gateSuccess, setGateSuccess] = useState(false); // eslint-disable-line

  const [showAGB, setShowAGB]   = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [copied, setCopied] = useState(false); // eslint-disable-line
  const [theme, setTheme]       = useState("dark");
  const [targetPct, setTargetPct] = useState(90);

  // Recalculate when targetPct changes after a result exists
  useEffect(() => {
    if (!result) return;
    const r = calculate(
      parseInt(form.age), parseInt(form.retirementAge),
      parseInt(form.gross),
      parseInt(form.monthlySavings) || 0,
      parseFloat(form.existingSavings) || 0,
      targetPct
    );
    setResult(r);
  }, [targetPct]); // eslint-disable-line

  const sliderResult = result
    ? calculate(
        parseInt(form.age), parseInt(form.retirementAge),
        parseInt(form.gross), slider,
        parseFloat(form.existingSavings) || 0,
        targetPct
      )
    : null;

  const coverage = sliderResult ? sliderResult.cov : (result ? result.cov : 0);
  const hasLump  = parseFloat(form.existingSavings) > 0;

  const retYears   = LE - (parseInt(form.retirementAge) || 67);
  const displayGap = result && sliderResult
    ? Math.round(Math.max(0, (result.cap - sliderResult.fvT) / Math.max(1, retYears * 12)))
    : (result ? result.gap : 0);

  function validate() {
    const e = {};
    const age      = parseInt(form.age);
    const ret      = parseInt(form.retirementAge);
    const gross    = parseInt(form.gross);
    const savings  = parseInt(form.monthlySavings) || 0;
    const lump     = parseFloat(form.existingSavings) || 0;

    if (!form.age || age < 16 || age > 60)
      e.age = "Zwischen 16 und 60";
    if (!form.retirementAge || isNaN(ret) || ret < 50 || ret > 80)
      e.retirementAge = "Zwischen 50 und 80";
    if (ret <= age)
      e.retirementAge = "Muss größer als dein Alter sein";
    if (ret - age < 5)
      e.retirementAge = "Mindestens 5 Jahre bis zur Rente";
    if (!form.gross || gross < 500)
      e.gross = "Mindestens 500 € Nettolohn";
    if (gross > 50000)
      e.gross = "Bitte monatliches Netto eingeben (nicht jährlich)";
    if (savings >= gross)
      e.monthlySavings = "Sparrate muss kleiner als dein Nettolohn sein";
    if (savings < 0)
      e.monthlySavings = "Keine negativen Werte";
    if (lump < 0)
      e.existingSavings = "Keine negativen Werte";
    return e;
  }

  function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    const r = calculate(
      parseInt(form.age), parseInt(form.retirementAge),
      parseInt(form.gross),
      parseInt(form.monthlySavings) || 0,
      parseFloat(form.existingSavings) || 0,
      targetPct
    );
    setResult(r);
    setSlider(parseInt(form.monthlySavings) || 0);
    if (hasUnlocked) {
      setUnlocked(true);
    } else {
      setUnlocked(false);
      setGateEmail(""); setGateConsent(false); setGateError(""); setGateSuccess(false);
    }
    setTimeout(() => document.getElementById("big-number")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }

  async function handleUnlock() {
    if (!gateEmail.includes("@") || !gateEmail.includes(".")) {
      setGateError("Bitte gültige E-Mail-Adresse eingeben");
      return;
    }
    if (!gateConsent) {
      setGateError("Bitte stimme der Datenspeicherung zu um fortzufahren");
      return;
    }
    setGateError("");
    setGateLoading(true);

    try {
      // Only call APIs if key is configured
      if (BREVO_API_KEY !== "DEIN_BREVO_API_KEY") {
        await Promise.all([
          addContactToBrevo(gateEmail),
          sendWelcomeEmail(gateEmail),
        ]);
      }
      setGateSuccess(true);
    } catch (err) {
      console.error("Brevo error:", err);
    } finally {
      setGateLoading(false);
      setHasUnlocked(true);
      setUnlocked(true);
      setTimeout(() => document.getElementById("big-number")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    }
  }

  const [showShareModal, setShowShareModal] = useState(false);
  const [cardDataUrl, setCardDataUrl] = useState(null);

  function generateCard(snapGap, snapCoverage, snapFvT, snapSlider, snapCap, snapIsPositive) {
    const W = 1080, H = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    const isDark = theme === "dark"; // eslint-disable-line
    const bg      = "#000000"; // always dark card — more dramatic
    const green   = "#00c875";
    const red     = "#ff3b30";
    const white   = "#ffffff";
    const sub     = "#888888";
    const surface = "#111111";
    const border  = "#1e1e1e";
    const font    = "Arial Black, Arial, sans-serif"; // reliable system font

    const isPositive = snapIsPositive;
    const gap = snapGap;

    // ── Background ──────────────────────────────────────────────────
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // ── Eyecatcher: big diagonal gradient blob ───────────────────────
    const grad = ctx.createRadialGradient(W * 0.75, H * 0.2, 0, W * 0.75, H * 0.2, 600);
    grad.addColorStop(0, isPositive ? "rgba(0,200,117,0.2)" : "rgba(255,59,48,0.18)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // ── Card inner surface ───────────────────────────────────────────
    ctx.fillStyle = surface;
    rr(ctx, 40, 40, W - 80, H - 80, 28);
    ctx.fill();

    // ── Top accent line ──────────────────────────────────────────────
    const accentColor = isPositive ? green : red;
    ctx.fillStyle = accentColor;
    rr(ctx, 40, 40, W - 80, 6, 3);
    ctx.fill();

    // ── Logo ─────────────────────────────────────────────────────────
    ctx.fillStyle = green;
    rr(ctx, 72, 90, 48, 48, 10);
    ctx.fill();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(82, 126); ctx.lineTo(90, 110);
    ctx.lineTo(97, 118); ctx.lineTo(110, 100);
    ctx.stroke();

    ctx.fillStyle = white;
    ctx.font = `700 32px ${font}`;
    ctx.fillText("pentiro.com", 132, 124);

    // ── Divider ──────────────────────────────────────────────────────
    ctx.strokeStyle = border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(72, 170); ctx.lineTo(W - 72, 170);
    ctx.stroke();

    // ── LABEL ────────────────────────────────────────────────────────
    ctx.fillStyle = sub;
    ctx.font = `500 22px ${font}`;
    ctx.letterSpacing = "2px";
    ctx.fillText("MEIN MONATLICHES PENTIRO", 72, 228);

    // ── BIG NUMBER (auto-size to fit) ─────────────────────────────────
    const numText = (isPositive ? "+" : "–") + Math.abs(gap).toLocaleString("de-DE") + " €";
    ctx.fillStyle = isPositive ? green : red;

    // Find fitting font size
    let fs = 180;
    ctx.font = `900 ${fs}px ${font}`;
    while (ctx.measureText(numText).width > W - 160 && fs > 80) {
      fs -= 8;
      ctx.font = `900 ${fs}px ${font}`;
    }
    ctx.fillText(numText, 72, 240 + fs);

    // ── Sub label under number ────────────────────────────────────────
    ctx.fillStyle = sub;
    ctx.font = `400 26px ${font}`;
    ctx.fillText(isPositive ? "pro Monat MEHR als ich brauche" : "pro Monat fehlen mir im Alter", 72, 260 + fs + 44);

    const baseY = 260 + fs + 44 + 48;

    // ── Horizontal rule ───────────────────────────────────────────────
    ctx.strokeStyle = border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(72, baseY); ctx.lineTo(W - 72, baseY);
    ctx.stroke();

    // ── 3 stat pills ──────────────────────────────────────────────────
    const stats = [
      { label: "Fehlendes Kapital",  value: eur(snapCap),                                    accent: red   },
      { label: "Kapital bei Rente",  value: eur(snapFvT),                                    accent: green },
      { label: "Meine Sparrate",     value: snapSlider > 0 ? eur(snapSlider)+"/Mo" : "0 €/Mo", accent: white },
    ];
    const pillW = (W - 160) / 3;
    const pillH = 120;
    const pillY = baseY + 28;

    stats.forEach((s, i) => {
      const px = 72 + i * (pillW + 8);
      ctx.fillStyle = "#181818";
      rr(ctx, px, pillY, pillW - 4, pillH, 14);
      ctx.fill();

      // accent left border
      ctx.fillStyle = s.accent;
      rr(ctx, px, pillY, 4, pillH, 2);
      ctx.fill();

      ctx.fillStyle = sub;
      ctx.font = `400 18px ${font}`;
      ctx.fillText(s.label, px + 18, pillY + 36);

      ctx.fillStyle = s.accent;
      ctx.font = `700 26px ${font}`;
      // Clamp value text
      let vText = s.value;
      ctx.font = `700 26px ${font}`;
      while (ctx.measureText(vText).width > pillW - 40 && vText.length > 4) {
        vText = vText.slice(0, -2);
      }
      ctx.fillText(vText, px + 18, pillY + 80);
    });

    // ── Eyecatcher quote ──────────────────────────────────────────────
    const quoteY = pillY + pillH + 44;
    ctx.fillStyle = isPositive ? "rgba(0,200,117,0.12)" : "rgba(255,59,48,0.1)";
    rr(ctx, 72, quoteY, W - 144, 100, 16);
    ctx.fill();
    ctx.strokeStyle = isPositive ? "rgba(0,200,117,0.35)" : "rgba(255,59,48,0.3)";
    ctx.lineWidth = 1;
    rr(ctx, 72, quoteY, W - 144, 100, 16);
    ctx.stroke();

    ctx.fillStyle = isPositive ? green : red;
    ctx.font = `700 26px ${font}`;
    ctx.textAlign = "center";
    const quote = isPositive
      ? `✓ Auf Kurs — mit ${snapSlider > 0 ? eur(snapSlider)+"/Mo" : "einem Sparplan"} abgesichert`
      : `Ich schließe meine Lücke. Was ist deine?`;
    ctx.fillText(quote, W / 2, quoteY + 58);
    ctx.textAlign = "left";

    // ── CTA bar bottom ─────────────────────────────────────────────────
    const ctaY = H - 40 - 80;
    ctx.fillStyle = green;
    rr(ctx, 72, ctaY, W - 144, 80, 16);
    ctx.fill();

    ctx.fillStyle = "#000";
    ctx.font = `700 28px ${font}`;
    ctx.textAlign = "center";
    ctx.fillText("Berechne dein Pentiro → pentiro.com", W / 2, ctaY + 50);
    ctx.textAlign = "left";

    return canvas.toDataURL("image/png");
  }

  function rr(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function handleShare() {
    // Compute fresh values at click time — no stale closure
    const snapSlider     = slider;
    const snapCap        = result ? result.cap : 0;
    const snapFvT        = sliderResult ? sliderResult.fvT : 0;
    const snapCoverage   = sliderResult ? sliderResult.cov : (result ? result.cov : 0);
    const snapRetYears   = LE - (parseInt(form.retirementAge) || 67);
    const snapIsPositive = snapCoverage >= 100;
    // Mirror exactly what the page displays
    const snapGap = result && sliderResult
      ? snapIsPositive
        ? Math.round((snapFvT - snapCap) / Math.max(1, snapRetYears * 12))  // surplus
        : Math.round(Math.max(0, (snapCap - snapFvT) / Math.max(1, snapRetYears * 12))) // deficit
      : (result ? result.gap : 0);

    const dataUrl = generateCard(snapGap, snapCoverage, snapFvT, snapSlider, snapCap, snapIsPositive);
    setCardDataUrl(dataUrl);

    // Try to share as image file on mobile
    if (navigator.share && navigator.canShare) {
      fetch(dataUrl)
        .then(r => r.blob())
        .then(blob => {
          const file = new File([blob], "pentiro-ergebnis.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            navigator.share({ files: [file], title: "Mein Pentiro Ergebnis" })
              .catch(() => setShowShareModal(true));
            return;
          }
          setShowShareModal(true);
        })
        .catch(() => setShowShareModal(true));
      return;
    }
    setShowShareModal(true);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={theme} style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
      {showAGB && <AGBModal onClose={() => setShowAGB(false)} />}

      {/* BERECHNUNG MODAL */}
      {showCalcModal && (
        <div className="modal-bg" onClick={() => setShowCalcModal(false)}>
          <div className="modal-box" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h2>Wie wird deine Rentenlücke berechnet?</h2>

            <h3>1. Erwartete Rente</h3>
            <p>Pentiro nutzt die offizielle <strong>Entgeltpunkte-Methode</strong> der Deutschen Rentenversicherung. Aus deinem Nettolohn wird dein Bruttolohn hochgerechnet, daraus deine Entgeltpunkte berechnet und mit dem aktuellen Rentenwert West (€ 39,32) multipliziert. Abzüglich ~8% Krankenversicherungsbeitrag ergibt sich deine reale Nettorente.</p>

            <h3>2. Benötigtes Kapital — 4%-Regel</h3>
            <p>Die monatliche Lücke (Zielrente − erwartete Rente) wird auf ein notwendiges Gesamtkapital hochgerechnet. Dabei nutzen wir die <strong>4%-Entnahmeregel</strong>: du entnimmst jährlich 4% deines Kapitals, das nach historischen Daten dauerhaft tragbar ist ohne das Kapital aufzubrauchen. Das benötigte Kapital = monatliche Lücke × 12 ÷ 4%.</p>

            <h3>3. ETF-Rendite und Steuern</h3>
            <p>Wir rechnen mit <strong>4,5% jährlicher Nominalrendite</strong> — realistisch für einen breit gestreuten ETF nach Kosten. Auf die Gewinne wird die <strong>Abgeltungssteuer (25% + Solidaritätszuschlag)</strong> abgezogen. Du siehst also was wirklich auf deinem Konto landet.</p>

            <h3>4. Was wir nicht modellieren</h3>
            <p>Individuelle Rentenbiographie, Erwerbsunterbrechungen, Betriebsrenten und staatliche Förderungen (Riester, Rürup) sind nicht berücksichtigt.</p>
            <p style={{ marginTop: 10, padding: "10px 14px", background: "var(--rdim)", border: "1px solid var(--rbdr)", borderRadius: 8 }}>
              <strong style={{ color: "var(--red)" }}>⚠ Inflation nicht berücksichtigt:</strong> Dein Zielwert basiert auf dem heutigen Geldwert. Bei 2% Inflation pro Jahr verliert €1.000 in 30 Jahren rund 45% seiner Kaufkraft. Die tatsächliche Lücke ist daher in der Realität eher <strong>größer</strong> als hier angezeigt.
            </p>

            <p style={{ fontSize: 12, marginTop: 16 }}>Keine Anlageberatung gemäß § 1 WpHG.</p>

            <button className="btn-green" onClick={() => setShowCalcModal(false)} style={{ marginTop: 8 }}>
              Verstanden
            </button>
          </div>
        </div>
      )}
      {showExplainer && (
        <div className="modal-bg" onClick={() => setShowExplainer(false)}>
          <div className="modal-box" style={{ maxWidth: 480, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <h2>Was ist die Rentenlücke?</h2>
            <p>Die <strong>Rentenlücke</strong> ist die Differenz zwischen deiner gesetzlichen Rente und dem Einkommen das du im Alter tatsächlich brauchst. In Deutschland deckt die gesetzliche Rente im Schnitt nur <strong>48 % des letzten Nettoeinkommens</strong> — die restlichen 52 % musst du selbst finanzieren.</p>

            <h3>Warum betrifft mich das?</h3>
            <p>Die Rentenformel wurde für eine Zeit konzipiert, in der viele Arbeitnehmer wenige Rentner finanzierten. Das Verhältnis hat sich umgekehrt. Wer heute 30 ist und nichts tut, wird im Alter dauerhaft mehrere hundert Euro pro Monat fehlen.</p>

            <h3>Was kann ich dagegen tun?</h3>
            <p>Der effektivste Weg ist ein <strong>ETF-Sparplan</strong> — regelmäßig monatlich investieren und den Zinseszins für sich arbeiten lassen. Je früher du anfängst, desto weniger musst du monatlich zurücklegen. Pentiro zeigt dir genau wie viel.</p>

            <h3>Wie genau ist die Berechnung?</h3>
            <p>Pentiro nutzt die offizielle Entgeltpunkte-Methode der Deutschen Rentenversicherung. Das Ergebnis ist eine fundierte Schätzung — keine Garantie. Die tatsächliche Rente hängt von deiner gesamten Erwerbsbiographie ab.</p>

            <button className="btn-green" onClick={() => setShowExplainer(false)} style={{ marginTop: 8 }}>
              Verstanden — jetzt berechnen
            </button>
          </div>
        </div>
      )}

      {/* SHARE CARD MODAL */}
      {showShareModal && cardDataUrl && (
        <div className="modal-bg" onClick={() => setShowShareModal(false)}>
          <div style={{
            background: "var(--s1)", border: "1px solid var(--b2)", borderRadius: "var(--rl)",
            maxWidth: 480, padding: 24, width: "100%", textAlign: "center",
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, letterSpacing: "-.5px" }}>
              Deine Ergebnis-Card
            </h2>
            <p style={{ color: "var(--sub)", fontSize: 13, marginBottom: 16 }}>
              Speicher das Bild und teile es auf Instagram, WhatsApp oder TikTok
            </p>

            {/* Card preview */}
            <img
              src={cardDataUrl}
              alt="Pentiro Ergebnis"
              style={{ width: "100%", borderRadius: 12, marginBottom: 16, border: "1px solid var(--b1)" }}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <a
                href={cardDataUrl}
                download="pentiro-ergebnis.png"
                style={{ flex: 1, textDecoration: "none" }}
              >
                <button className="btn-green" style={{ width: "100%" }}>
                  ↓ Bild speichern
                </button>
              </a>
              <button className="btn-ghost" onClick={() => setShowShareModal(false)}>
                Schließen
              </button>
            </div>

            <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 12 }}>
              Drücke lange auf das Bild auf dem Handy um es zu speichern
            </p>
          </div>
        </div>
      )}

      {/* NAV */}
      <header style={{
        alignItems: "center", background: theme === "dark" ? "rgba(8,8,8,.92)" : "rgba(255,255,255,.92)", backdropFilter: "blur(16px)",
        borderBottom: "1px solid var(--b1)", display: "flex", height: 60,
        justifyContent: "space-between", padding: "0 24px",
        position: "sticky", top: 0, zIndex: 200,
      }}>
        <div style={{ alignItems: "center", display: "flex", gap: 10 }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <rect width="28" height="28" rx="8" fill="var(--green)" />
            <polyline points="6,20 11,12 15,16 22,8"
              stroke="#000" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <div>
            <span style={{ color: "var(--white)", fontSize: 15, fontWeight: 700, letterSpacing: "-.2px", display: "block", lineHeight: 1.1 }}>
              pentiro.com
            </span>
            <span style={{ color: "var(--muted)", fontSize: 10, fontWeight: 500, letterSpacing: ".2px" }}>
              Dein Rentenlücken-Rechner
            </span>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button
            onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
            style={{
              background: theme === "dark" ? "var(--s2)" : "var(--s2)",
              border: "1.5px solid var(--b2)",
              borderRadius: 8,
              color: "var(--text)",
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "var(--font)",
              fontWeight: 600,
              padding: "6px 12px",
              transition: "background .2s, border-color .2s",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
          </button>
          <span className="hide-mobile" style={{ color:"var(--sub)", fontSize:12, fontWeight:500 }}>Keine Anlageberatung</span>
        </div>
      </header>

      {/* HERO */}
      <main style={{ maxWidth: 560, margin: "0 auto", padding: "72px 20px 48px" }}>
        <div className="au" style={{ marginBottom: 48, textAlign: "center" }}>

          {/* Emotional hook — rotating stories */}
          <EmotionalHook />

          <div style={{
            color: "var(--sub)", display: "inline-block", fontSize: 13, fontWeight: 500,
            letterSpacing: .2, marginBottom: 20,
          }}>
            Kostenlos · Kein Login · 30 Sekunden
          </div>
          <h1 style={{
            color: "var(--text)", fontSize: "clamp(36px,6vw,58px)", fontWeight: 900,
            letterSpacing: "-2px", lineHeight: 1.05, marginBottom: 20,
          }}>
            Wie groß ist deine<br />
            <span style={{ color: "var(--green)" }}>Rentenlücke?</span>
          </h1>
          <p style={{ color: "var(--sub)", fontSize: 16, lineHeight: 1.6, maxWidth: 380, margin: "0 auto", fontWeight: 400 }}>
            Die gesetzliche Rente deckt nur{" "}
            <strong style={{ color: "var(--text)", fontWeight: 700 }}>48 %</strong> deines Einkommens.
            Wer zahlt die restlichen{" "}
            <strong style={{ color: "var(--text)", fontWeight: 700 }}>52 %</strong>?
          </p>
          <button
            onClick={() => setShowExplainer(true)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--green)", fontSize: 13, fontWeight: 600,
              marginTop: 12, textDecoration: "underline", fontFamily: "var(--font)",
              letterSpacing: ".1px",
            }}
          >
            Was ist die Rentenlücke?
          </button>
        </div>

        {/* FORM */}
        <div className="au d1" style={{ marginBottom: 14 }}>

          {/* 4 main inputs in 2x2 grid */}
          <div className="g2" style={{ marginBottom: 36, gap: 32 }}>
            <div>
              <p className="lbl">Alter</p>
              <input className="field" type="number" placeholder="26" min="16" max="60"
                value={form.age}
                onChange={e => setForm(f => ({ ...f, age: e.target.value }))} />
              {errors.age && <p className="err">{errors.age}</p>}
            </div>
            <div>
              <p className="lbl">Rentenalter</p>
              <input className="field" type="number" placeholder="67" min="50" max="80"
                value={form.retirementAge}
                onChange={e => setForm(f => ({ ...f, retirementAge: e.target.value }))} />
              {errors.retirementAge && <p className="err">{errors.retirementAge}</p>}
            </div>
            <div>
              <p className="lbl">Netto / Monat</p>
              <div style={{ position: "relative" }}>
                <input className="field" type="number" placeholder="2.200" min="500"
                  value={form.gross}
                  onChange={e => setForm(f => ({ ...f, gross: e.target.value }))} />
                <span style={{ position: "absolute", right: 0, bottom: 10, color: "var(--muted)", fontSize: 16, fontWeight: 400 }}>€</span>
              </div>
              {errors.gross && <p className="err">{errors.gross}</p>}
            </div>
            <div>
              <p className="lbl">Sparrate / Monat</p>
              <div style={{ position: "relative" }}>
                <input className="field" type="number" placeholder="0" min="0"
                  value={form.monthlySavings}
                  onChange={e => setForm(f => ({ ...f, monthlySavings: e.target.value }))} />
                <span style={{ position: "absolute", right: 0, bottom: 10, color: "var(--muted)", fontSize: 16, fontWeight: 400 }}>€</span>
              </div>
              {errors.monthlySavings && <p className="err">{errors.monthlySavings}</p>}
            </div>
          </div>

          {/* Existing savings — minimal */}
          <div style={{ marginBottom: 36 }}>
            <p className="lbl">Vorhandene Ersparnisse <span style={{ color: "var(--muted)", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>— optional</span></p>
            <div style={{ position: "relative" }}>
              <input className="field" type="number" placeholder="0" min="0"
                value={form.existingSavings}
                onChange={e => setForm(f => ({ ...f, existingSavings: e.target.value }))} />
              <span style={{ position: "absolute", right: 0, bottom: 10, color: "var(--muted)", fontSize: 16, fontWeight: 400 }}>€</span>
            </div>
            {errors.existingSavings && <p className="err">{errors.existingSavings}</p>}
          </div>

          {/* Ziel-Rente slider */}
          <div style={{ marginBottom: 40 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <p className="lbl" style={{ marginBottom: 0 }}>Ziel-Rente</p>
              <span style={{ color: "var(--text)", fontSize: 28, fontWeight: 800, letterSpacing: "-1px" }}>
                {targetPct} <span style={{ fontSize: 16, fontWeight: 500, color: "var(--sub)" }}>%</span>
              </span>
            </div>
            <input className="sl" type="range" min="60" max="100" step="5"
              value={targetPct}
              onChange={e => setTargetPct(parseInt(e.target.value))} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <span style={{ color: "var(--muted)", fontSize: 11 }}>60 % Grundbedarf</span>
              <span style={{ color: "var(--muted)", fontSize: 11 }}>100 % gleicher Standard</span>
            </div>
          </div>

          <button className="btn-green" onClick={handleSubmit}>
            <Arr c="#000" />
            Kostenlos berechnen — dauert 30 Sekunden
          </button>
        </div>

        {/* SOCIAL PROOF */}
        <div className="au d2" style={{ marginTop: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          {/* Rotating success story */}
          <SuccessHook />

          {/* User count */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex" }}>
              {["#4a9eff","#ff6b6b","#ffd166","#06d6a0","#a29bfe"].map((c,i) => (
                <div key={i} style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: c, border: "2px solid var(--bg)",
                  marginLeft: i > 0 ? -8 : 0, fontSize: 12,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#000", fontWeight: 700,
                }}>
                  {["M","L","S","K","T"][i]}
                </div>
              ))}
            </div>
            <p style={{ color: "var(--sub)", fontSize: 13 }}>
              <strong style={{ color: "var(--text)" }}>2.847</strong> Berechnungen diese Woche
            </p>
          </div>
          {/* Trust line */}
          <p style={{ color: "var(--muted)", fontSize: 11, textAlign: "center" }}>
            Kein Login · Keine Datenspeicherung · DSGVO-konform
          </p>
        </div>
      </main>

      {/* RESULTS */}
      {result && (
        <section id="results" style={{ maxWidth: 580, margin: "0 auto", padding: "0 20px 140px", scrollMarginTop: 72 }}>
          <div className="hr" style={{ marginBottom: 48 }} />

          {/* STAGE 1 — Big number always visible, no blur, full shock moment */}
          <div id="big-number" className="au" style={{ textAlign: "center", marginBottom: 28 }}>
            <p style={{ color: "var(--sub)", fontSize: 12, fontWeight: 700, letterSpacing: .4, marginBottom: 14, textTransform: "uppercase" }}>
              Dein monatliches Pentiro
            </p>
            {coverage >= 100 ? (
              // POSITIVE — surplus
              <>
                <div style={{ color: "var(--green)", fontSize: "clamp(52px,10vw,80px)", fontWeight: 800, letterSpacing: "-3px", lineHeight: 1, marginBottom: 8 }}>
                  +{eur(Math.round((sliderResult.fvT - result.cap) / Math.max(1, retYears * 12)))}
                </div>
                <p style={{ color: "var(--green)", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                  pro Monat <em>mehr</em> als du brauchst — du bist auf Kurs 🎯
                </p>
                <SurplusContext surplus={Math.round((sliderResult.fvT - result.cap) / Math.max(1, retYears * 12))} />
                <p style={{ color: "var(--sub)", fontSize: 13, marginTop: 10 }}>
                  Bei Rente: <strong style={{ color: "var(--text)" }}>{eur(sliderResult.fvT)}</strong> angespart · Ziel: {eur(result.cap)}
                </p>
                <button onClick={() => setShowCalcModal(true)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--muted)", fontSize:11, fontWeight:500, fontFamily:"var(--font)", textDecoration:"underline", padding:0, marginTop:6 }}>Wie wird das berechnet?</button>
              </>
            ) : displayGap === 0 && result.gap === 0 ? (
              // ZERO — exactly break-even
              <>
                <div style={{ color: "var(--amber)", fontSize: "clamp(52px,10vw,80px)", fontWeight: 800, letterSpacing: "-3px", lineHeight: 1, marginBottom: 8 }}>
                  ± 0 €
                </div>
                <p style={{ color: "var(--amber)", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                  Du stehst auf der Kippe ⚠️
                </p>
                <p style={{ color: "var(--sub)", fontSize: 14 }}>
                  Deine Rente deckt deinen Bedarf — aber nur knapp. Ein Jobwechsel oder eine Rentenreform reicht.
                </p>
              </>
            ) : (
              // NEGATIVE — gap with context
              <>
                <div style={{ color: "var(--red)", fontSize: "clamp(58px,12vw,92px)", fontWeight: 800, letterSpacing: "-3px", lineHeight: 1, marginBottom: 8 }}>
                  –{displayGap.toLocaleString("de-DE")}€
                </div>
                <p style={{ color: "var(--sub)", fontSize: 14, marginBottom: 12 }}>
                  pro Monat fehlen dir im Alter
                  {slider > 0 && <span style={{ color: "var(--sub)", fontSize: 12 }}> · ohne Sparrate: {eur(result.gap)}</span>}
                </p>
                {/* Contextual translation */}
                <GapContext gap={displayGap} />
              </>
            )}
          </div>

          {/* Quick stats — always visible */}
          <div className="g2 au d1" style={{ marginBottom: 28 }}>
            <div className="pill">
              <p className="lbl">Erwartete Rente</p>
              <p style={{ color: "var(--amber)", fontSize: 24, fontWeight: 800, letterSpacing: "-.8px" }}>
                {eur(result.pen)}<span style={{ color: "var(--sub)", fontSize: 13, fontWeight: 400 }}>/Mo</span>
              </p>
            </div>
            <div className="pill">
              <p className="lbl">Dein Ziel</p>
              <p style={{ color: "var(--text)", fontSize: 24, fontWeight: 800, letterSpacing: "-.8px" }}>
                {eur(result.des)}<span style={{ color: "var(--sub)", fontSize: 13, fontWeight: 400 }}>/Mo</span>
              </p>
            </div>
          </div>

          {/* INLINE GATE — adaptive messaging */}
          {!unlocked && (
            <div className="af" style={{ marginBottom: 24 }}>
              <div style={{
                background: "var(--s1)", border: `1px solid ${coverage >= 100 ? "var(--gbdr)" : displayGap === 0 && result.gap === 0 ? "rgba(255,149,0,.3)" : "var(--rbdr)"}`,
                borderRadius: "var(--rl)", padding: "28px 24px", textAlign: "center",
              }}>
                {/* Icon */}
                <div style={{
                  background: coverage >= 100 ? "var(--gdim)" : displayGap === 0 && result.gap === 0 ? "rgba(255,149,0,.1)" : "var(--rdim)",
                  border: `1px solid ${coverage >= 100 ? "var(--gbdr)" : displayGap === 0 && result.gap === 0 ? "rgba(255,149,0,.3)" : "var(--rbdr)"}`,
                  borderRadius: 12, display: "inline-flex", alignItems: "center",
                  justifyContent: "center", marginBottom: 16, padding: 12,
                }}>
                  {coverage >= 100 ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M3 13l4 4L17 7" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" stroke="var(--green)" strokeWidth="1.8"/>
                    </svg>
                  ) : displayGap === 0 && result.gap === 0 ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9v4M12 17h.01" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="var(--amber)" strokeWidth="1.8"/>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9v4M12 17h.01" stroke="var(--red)" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="var(--red)" strokeWidth="1.8"/>
                    </svg>
                  )}
                </div>

                {/* Adaptive headline */}
                <h3 style={{ color: "var(--text)", fontSize: 18, fontWeight: 800, letterSpacing: "-.5px", marginBottom: 8 }}>
                  {coverage >= 100
                    ? "Sieh wie dein Kapital wächst"
                    : displayGap === 0 && result.gap === 0
                    ? "Sieh wie du dich absicherst"
                    : "Zeig mir wie ich die Lücke schließe"}
                </h3>
                <p style={{ color: "var(--sub)", fontSize: 14, lineHeight: 1.6, maxWidth: 300, margin: "0 auto 20px" }}>
                  {coverage >= 100
                    ? "Erhalte deinen persönlichen Sparplan und sieh wie du deinen Überschuss optimal anlegst."
                    : displayGap === 0 && result.gap === 0
                    ? "Du stehst knapp auf der Kippe — sieh wie ein kleiner Sparplan dich dauerhaft absichert."
                    : `${eur(result.cap)} fehlendes Kapital — sieh konkret wie du die Lücke mit einem Sparplan schließt.`}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 340, margin: "0 auto" }}>
                  <input
                    id="gate-input"
                    className="field"
                    type="email"
                    placeholder="deine@email.de"
                    value={gateEmail}
                    onChange={e => { setGateEmail(e.target.value); setGateError(""); }}
                    style={{ textAlign: "center", borderBottomColor: coverage >= 100 ? "var(--gbdr)" : "var(--rbdr)" }}
                  />
                  <label className="cb-wrap" style={{ textAlign: "left" }}>
                    <input className="cb-box" type="checkbox" checked={gateConsent}
                      onChange={e => { setGateConsent(e.target.checked); setGateError(""); }} />
                    <span style={{ color: "var(--sub)", fontSize: 12, lineHeight: 1.55 }}>
                      Ich bin einverstanden mit der Speicherung meiner E-Mail für Updates und Angebote.{" "}
                      <span onClick={e => { e.preventDefault(); e.stopPropagation(); setShowAGB(true); }}
                        style={{ color: "var(--green)", cursor: "pointer", textDecoration: "underline" }}>
                        Datenschutz
                      </span>
                    </span>
                  </label>
                  {gateError && <p style={{ color: "var(--red)", fontSize: 13, fontWeight: 500, textAlign: "center" }}>{gateError}</p>}
                  <button className="btn-green" type="button" disabled={gateLoading} onClick={handleUnlock}
                    style={{ background: "var(--green)" }}>
                    {gateLoading
                      ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                          <circle cx="8" cy="8" r="6" stroke="#000" strokeWidth="2" strokeDasharray="25" strokeDashoffset="10"/>
                        </svg>
                      : <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                          <path d="M13 7.5L8 3M13 7.5L8 12M13 7.5H2" stroke="#000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    }
                    {gateLoading ? "Wird verarbeitet…"
                      : coverage >= 100 ? "Sparplan ansehen →"
                      : displayGap === 0 && result.gap === 0 ? "Absicherung ansehen →"
                      : "Lösungsweg freischalten →"}
                  </button>
                </div>
                <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 12 }}>
                  Kein Spam · Abmeldung jederzeit · DSGVO-konform
                </p>
              </div>
            </div>
          )}

          {/* REST — blurred until unlocked */}
          <div className={unlocked ? "revealed" : "blurred"} style={{ pointerEvents: unlocked ? "auto" : "none" }}>

            {/* Delay cost — what waiting costs */}
            {unlocked && displayGap > 0 && (
              <DelayCost monthlyGap={displayGap} age={parseInt(form.age) || 30} />
            )}

              {/* CTA 1 — under number, adapts to gap vs surplus */}
              {displayGap > 0 ? (
                // Gap — urgent CTA
                <a href="https://traderepublic.com" target="_blank" rel="noopener noreferrer"
                  style={{ textDecoration:"none", display:"block", marginBottom:20 }}>
                  <div style={{
                    background:"var(--green)", borderRadius:"var(--r)",
                    padding:"14px 20px", display:"flex",
                    alignItems:"center", justifyContent:"space-between",
                    transition:"opacity .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.opacity=".88"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                  >
                    <div>
                      <p style={{color:"#000",fontSize:14,fontWeight:800,marginBottom:2}}>
                        Jetzt Lücke schließen — ETF-Sparplan eröffnen
                      </p>
                      <p style={{color:"rgba(0,0,0,.55)",fontSize:12,fontWeight:500}}>
                        Trade Republic · kostenlos · ab 1 €/Monat
                      </p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10h12M11 5l5 5-5 5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </a>
              ) : (
                // Covered — aspirational CTA
                <a href="https://traderepublic.com" target="_blank" rel="noopener noreferrer"
                  style={{ textDecoration:"none", display:"block", marginBottom:20 }}>
                  <div style={{
                    background:"var(--s1)", border:"1.5px solid var(--green)",
                    borderRadius:"var(--r)", padding:"14px 20px",
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    transition:"background .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"}
                    onMouseLeave={e=>e.currentTarget.style.background="var(--s1)"}
                  >
                    <div>
                      <p style={{color:"var(--green)",fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:4}}>
                        Gut aufgestellt — jetzt optimieren
                      </p>
                      <p style={{color:"var(--text)",fontSize:14,fontWeight:700,marginBottom:2}}>
                        Dein Überschuss kann noch mehr wachsen
                      </p>
                      <p style={{color:"var(--sub)",fontSize:12}}>
                        Trade Republic · 4 % aufs Verrechnungskonto · kostenlos
                      </p>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10h12M11 5l5 5-5 5" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </a>
              )}

              {/* CAPITAL */}
              <div className="pill au d2" style={{ marginBottom: 12, padding: "20px 22px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: hasLump ? 16 : 0 }}>
                  <div>
                    <p className="lbl">Fehlendes Gesamtkapital</p>
                    <p style={{ color: "var(--red)", fontSize: 28, fontWeight: 800, letterSpacing: "-1px" }}>
                      <CountUp target={result.cap} duration={2100} suffix=" €" />
                    </p>
                  </div>
                  <div style={{ background: "var(--rdim)", border: "1px solid var(--rbdr)", borderRadius: 10, flexShrink: 0, padding: "10px 14px", textAlign: "center" }}>
                    <p style={{ color: "var(--red)", fontSize: 22, fontWeight: 800 }}>{result.yL}</p>
                    <p style={{ color: "var(--sub)", fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>Jahre<br />verbleibend</p>
                  </div>
                </div>

                {hasLump && sliderResult && (
                  <div style={{ borderTop: "1px solid var(--b2)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                    <p className="lbl">Kapital bei Rente (6 % p.a.)</p>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "var(--sub)", fontSize: 13 }}>Ersparnisse heute ({eur(parseFloat(form.existingSavings))})</span>
                      <span style={{ background: "var(--gdim)", borderRadius: 6, color: "var(--green)", fontSize: 13, fontWeight: 700, padding: "2px 8px" }}>→ {eur(sliderResult.fvL)}</span>
                    </div>
                    {slider > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: "var(--sub)", fontSize: 13 }}>Sparrate ({eur(slider)}/Monat)</span>
                        <span style={{ background: "var(--gdim)", borderRadius: 6, color: "var(--green)", fontSize: 13, fontWeight: 700, padding: "2px 8px" }}>→ {eur(sliderResult.fvM)}</span>
                      </div>
                    )}
                    <div style={{ borderTop: "1px solid var(--b2)", paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "var(--text)", fontSize: 14, fontWeight: 700 }}>Gesamtkapital bei Rente</span>
                      <span style={{ color: "var(--green)", fontSize: 15, fontWeight: 800 }}>{eur(sliderResult.fvT)}</span>
                    </div>
                    <div style={{
                      background: sliderResult.cov >= 100 ? "var(--gdim)" : "var(--rdim)",
                      border: `1px solid ${sliderResult.cov >= 100 ? "var(--gbdr)" : "var(--rbdr)"}`,
                      borderRadius: 10, padding: "10px 14px", textAlign: "center",
                    }}>
                      <p style={{ color: sliderResult.cov >= 100 ? "var(--green)" : "var(--text)", fontSize: 13, fontWeight: 600 }}>
                        {sliderResult.cov >= 100
                          ? "✓ Deine Lücke ist mit deinen Ersparnissen gedeckt"
                          : `Noch fehlen ${eur(sliderResult.add)}/Monat zusätzlich`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* SLIDER */}
              <div className="card au d3" style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: "var(--white)", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Was wäre wenn ich spare…</h3>
                    <p style={{ color: "var(--sub)", fontSize: 13 }}>Ziehe den Slider und sieh wie die Lücke schmilzt</p>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ color: "var(--green)", fontSize: 22, fontWeight: 800, letterSpacing: "-.5px" }}>{eur(slider)}</p>
                    <p style={{ color: "var(--sub)", fontSize: 12 }}>/ Monat</p>
                  </div>
                </div>

                <input className="sl" type="range" min="0" step="10"
                  max={Math.max(Math.ceil(result.sNeed * 1.6 / 50) * 50, 500)}
                  value={slider}
                  onChange={e => setSlider(parseInt(e.target.value))}
                  style={{ marginBottom: 20 }} />

                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: "var(--sub)", fontWeight: 500 }}>Lücke gedeckt</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: coverage >= 100 ? "var(--green)" : "var(--text)" }}>{coverage} %</span>
                  </div>
                  <div className="track">
                    <div className="fill" style={{
                      width: `${coverage}%`,
                      background: coverage >= 100 ? "var(--green)" : "linear-gradient(90deg,var(--red) 0%,var(--amber) 55%,var(--green) 100%)",
                    }} />
                  </div>
                </div>

                {sliderResult && (
                  <div className="g2">
                    <div style={{ background: "var(--bg)", borderRadius: 10, padding: "12px 16px" }}>
                      <p style={{ color: "var(--sub)", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Noch fehlende Sparrate</p>
                      <p style={{ color: coverage >= 100 ? "var(--green)" : "var(--red)", fontSize: 18, fontWeight: 800, letterSpacing: "-.5px" }}>
                        {coverage >= 100 ? "✓ Gedeckt" : `${eur(sliderResult.add)}/Mo`}
                      </p>
                    </div>
                    <div style={{ background: "var(--bg)", borderRadius: 10, padding: "12px 16px" }}>
                      <p style={{ color: "var(--sub)", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Kapital bei Rente</p>
                      <p style={{ color: "var(--green)", fontSize: 18, fontWeight: 800, letterSpacing: "-.5px" }}>{eur(sliderResult.fvT)}</p>
                    </div>
                  </div>
                )}
              </div>


              {/* WACHSTUMS-CHART */}
              <GrowthChart
                age={parseInt(form.age)}
                retirementAge={parseInt(form.retirementAge)}
                monthlySavings={slider}
                existingSavings={parseFloat(form.existingSavings) || 0}
                theme={theme}
              />

              {/* VERZICHT-RECHNER */}
              {slider > 0 && <SacrificeCalc monthly={slider} />}

              {/* CTA 2 — after zinseszins moment */}
              <a href="https://scalable.capital" target="_blank" rel="noopener noreferrer"
                style={{ textDecoration:"none", display:"block", marginBottom:14 }}>
                <div style={{
                  background:"var(--s1)", border:"1.5px solid var(--green)",
                  borderRadius:"var(--r)", padding:"16px 20px",
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                  transition:"background .15s",
                }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--s1)"}
                >
                  <div>
                    <p style={{color:"var(--green)",fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:4}}>
                      Zinseszins nutzen
                    </p>
                    <p style={{color:"var(--white)",fontSize:14,fontWeight:700}}>
                      Scalable Capital — 3 Monate Prime gratis
                    </p>
                    <p style={{color:"var(--sub))",fontSize:12,marginTop:2}}>
                      Kostenloses Depot · ETF-Sparplan ab 1 €
                    </p>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{flexShrink:0}}>
                    <path d="M3 9h12M9 4l5 5-5 5" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </a>

              {/* SHARE */}
              <div className="card au d4" style={{
                marginBottom: 14,
                background: "var(--s1)",
                borderColor: "var(--b2)", position: "relative", overflow: "hidden",
              }}>
                {/* subtle green glow top-left */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "50%", background: "radial-gradient(ellipse at 20% 0%,rgba(0,200,117,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

                <p className="lbl" style={{ marginBottom: 12 }}>
                  {slider > 0 ? `Wenn du heute ${eur(slider)}/Monat sparst…` : "Wenn du heute anfängst zu sparen…"}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {/* Gain row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "var(--sub)", fontSize: 14 }}>Kapital bei Rente</span>
                    <span style={{ color: "var(--green)", fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px" }}>
                      {eur(sliderResult ? sliderResult.fvT : 0)}
                    </span>
                  </div>
                  {/* Zinseszins row */}
                  {sliderResult && sliderResult.fvT > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "var(--sub)", fontSize: 14 }}>Davon gratis durch Zinseszins</span>
                      <span style={{ color: "var(--green)", fontSize: 16, fontWeight: 700 }}>
                        +{eur(Math.max(0, sliderResult.fvT - (slider * (parseInt(form.retirementAge) - parseInt(form.age)) * 12 + (parseFloat(form.existingSavings) || 0))))}
                      </span>
                    </div>
                  )}
                  {/* Coverage */}
                  <div style={{
                    background: coverage >= 100 ? "var(--gdim)" : "var(--rdim)",
                    border: `1px solid ${coverage >= 100 ? "var(--gbdr)" : "var(--rbdr)"}`,
                    borderRadius: 8, padding: "8px 14px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span style={{ color: coverage >= 100 ? "var(--green)" : "var(--sub)", fontSize: 13, fontWeight: 600 }}>
                      {coverage >= 100 ? "✓ Rentenlücke vollständig gedeckt" : `${coverage} % der Rentenlücke gedeckt`}
                    </span>
                    {coverage < 100 && (
                      <span style={{ color: "var(--red)", fontSize: 13, fontWeight: 700 }}>
                        noch {eur(result.sNeed - slider)}/Mo fehlen
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="btn-green" onClick={handleShare} style={{ flex: 1 }}>
                    {copied ? "✓ Kopiert!" : "Ergebnis teilen"}
                  </button>
                  <button className="btn-ghost" onClick={() => { setResult(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                    Neu berechnen
                  </button>
                </div>
                <p style={{ color: "var(--sub)", fontSize: 12, marginTop: 12, textAlign: "center" }}>
                  Teile es — die meisten deiner Freunde kennen ihre Zahl nicht
                </p>
              </div>

              {/* AFFILIATES */}
              <div>
                <p className="lbl" style={{ textAlign: "center", marginBottom: 12 }}>Lücke schließen — jetzt starten</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { name: "Trade Republic", badge: "Beliebteste Wahl", dot: "#00c875", sub: "ETF-Sparplan ab 1 € · 4 % auf Verrechnungskonto", href: "https://traderepublic.com" },
                    { name: "Scalable Capital", badge: "Empfohlen", dot: "#00c875", sub: "Kostenloser Sparplan · 3 Monate Prime gratis", href: "https://scalable.capital" },
                    { name: "ING Depot", badge: "", dot: "var(--muted)", sub: "Kostenlose ETF-Sparpläne · Traditionsbank", href: "https://ing.de" },
                  ].map((a, i) => (
                    <a key={i} href={a.href} target="_blank" rel="noopener noreferrer" className="aff">
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: a.dot, boxShadow: a.dot !== "var(--muted)" ? `0 0 6px ${a.dot}` : "none" }} />
                        <div>
                          <div style={{ alignItems: "center", display: "flex", gap: 8, marginBottom: 3 }}>
                            <span style={{ color: "var(--white)", fontSize: 15, fontWeight: 700 }}>{a.name}</span>
                            {a.badge && <span className="badge">{a.badge}</span>}
                          </div>
                          <p style={{ color: "var(--sub)", fontSize: 13 }}>{a.sub}</p>
                        </div>
                      </div>
                      <Arr />
                    </a>
                  ))}
                </div>
                <p style={{ color: "var(--muted2)", fontSize: 11, fontWeight: 500, marginTop: 14, textAlign: "center", lineHeight: 1.6 }}>
                  Affiliate-Links · Keine Mehrkosten für dich · Keine Anlageberatung
                </p>
              </div>

            </div>{/* end blurred/revealed */}

        </section>
      )}

      {/* CTA 3 — sticky bottom bar */}
      {result && unlocked && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0,
          background: theme === "dark" ? "rgba(0,0,0,.96)" : "rgba(255,255,255,.97)",
          backdropFilter:"blur(16px)",
          borderTop:"1px solid var(--b1)",
          padding:"12px 20px",
          zIndex:300,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <div style={{maxWidth:540,width:"100%",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            {coverage >= 100 ? (
              <>
                <div style={{flex:1,minWidth:160}}>
                  <p style={{color:"var(--green)",fontSize:13,fontWeight:700,lineHeight:1.3}}>
                    ✓ Gut aufgestellt — jetzt Rendite maximieren
                  </p>
                  <p style={{color:"var(--sub)",fontSize:11}}>4 % Zinsen auf dein Guthaben bei Trade Republic</p>
                </div>
                <a href="https://traderepublic.com" target="_blank" rel="noopener noreferrer" style={{textDecoration:"none",flexShrink:0}}>
                  <div style={{
                    background:"var(--green)",borderRadius:10,padding:"10px 20px",cursor:"pointer",transition:"opacity .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                  >
                    <p style={{color:"#000",fontSize:14,fontWeight:800,whiteSpace:"nowrap"}}>Konto eröffnen →</p>
                  </div>
                </a>
              </>
            ) : (
              <>
                <div style={{flex:1,minWidth:160}}>
                  <p style={{color:"var(--text)",fontSize:13,fontWeight:700,lineHeight:1.3}}>
                    Lücke: <span style={{color:"var(--red)"}}>{eur(displayGap)}/Monat</span>
                  </p>
                  <p style={{color:"var(--sub)",fontSize:11}}>Jetzt handeln — nicht warten</p>
                </div>
                <a href="https://traderepublic.com" target="_blank" rel="noopener noreferrer" style={{textDecoration:"none",flexShrink:0}}>
                  <div style={{
                    background:"var(--green)",borderRadius:10,padding:"10px 20px",cursor:"pointer",transition:"opacity .15s",
                  }}
                    onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                  >
                    <p style={{color:"#000",fontSize:14,fontWeight:800,whiteSpace:"nowrap"}}>Sparplan starten →</p>
                  </div>
                </a>
              </>
            )}
          </div>
        </div>
      )}

      {/* TRUST STATS */}
      {!result && (
        <section style={{ maxWidth: 560, margin: "0 auto", padding: "0 20px 80px" }}>
          <div className="hr" style={{ marginBottom: 40 }} />
          <div className="g3" style={{ marginBottom: 32 }}>
            {[
              { n: "∅ –€580", l: "monatliche Rentenlücke" },
              { n: "87 %",    l: "unterschätzen ihre Lücke" },
              { n: "30 Sek",  l: "bis zu deiner Zahl" },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <p style={{ color: "var(--text)", fontSize: 22, fontWeight: 800, letterSpacing: "-.5px", lineHeight: 1.2, marginBottom: 4 }}>{s.n}</p>
                <p style={{ color: "var(--muted)", fontSize: 11, fontWeight: 500, lineHeight: 1.4 }}>{s.l}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SEO content — hidden from users, crawlable by Google */}
      <div style={{ position:"absolute", width:1, height:1, overflow:"hidden", opacity:0, pointerEvents:"none" }} aria-hidden="true">
        <h2>Was ist eine Rentenlücke?</h2>
        <p>Die Rentenlücke ist die Differenz zwischen der gesetzlichen Rente und dem tatsächlich benötigten Einkommen im Alter. In Deutschland deckt die gesetzliche Rente im Schnitt nur 48 % des letzten Nettoeinkommens.</p>
        <h3>Rentenlücke berechnen</h3>
        <p>Pentiro berechnet deine persönliche Rentenlücke kostenlos in 30 Sekunden. Kein Login, keine Datenspeicherung.</p>
        <h3>Ab wann sollte ich mit privater Altersvorsorge anfangen?</h3>
        <p>So früh wie möglich. Durch den Zinseszins-Effekt reicht bei frühem Start deutlich weniger monatliche Sparrate.</p>
      </div>

      <footer style={{ borderTop: "1px solid var(--b1)", padding: "24px 20px", textAlign: "center" }}>
        <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.8 }}>
          © 2026 pentiro.com ·{" "}
          <span onClick={() => setShowAGB(true)} style={{ color: "var(--sub)", cursor: "pointer", textDecoration: "underline" }}>Impressum</span> ·{" "}
          <span onClick={() => setShowAGB(true)} style={{ color: "var(--sub)", cursor: "pointer", textDecoration: "underline" }}>Datenschutz</span> ·{" "}
          <span onClick={() => setShowAGB(true)} style={{ color: "var(--sub)", cursor: "pointer", textDecoration: "underline" }}>AGB</span>
          <br />
          <span style={{ color: "var(--muted)", fontSize: 11 }}>
            Keine Anlageberatung (§ 1 WpHG) · Berechnung basiert auf DRV-Entgeltpunkte-Methode
          </span>
        </p>
      </footer>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
